import { useCallback, useEffect, useState } from 'react'

import { useCreateProductMessage } from './useCreateProductMessage'
import { useDateTimeValidation } from './useDateTimeValidation'
import { useDeleteProductMessage } from './useDeleteProductMessage'
import { UseFormMethods, useForm } from 'react-hook-form'

import { add } from 'date-fns'

import { ProductMessagesQuery } from '@/graphql/generated/operations'
import {
  formatDateForBackend,
  parseUTCToPST,
} from '@/utils/timezone/pstTimezone'

/**
 * Type for a single product message
 */
export type ProductMessage = ProductMessagesQuery['productMessages'][number]

/**
 * Form data type for announcement banner
 */
export type AnnouncementBannerFormData = {
  message: string
}

/**
 * Date/time state and handlers
 */
export interface DateTimeStateIProps {
  startDate: Date
  startTime: Date
  endDate: Date
  endTime: Date
  setStartDate: (date: Date) => void
  setStartTime: (time: Date) => void
  setEndDate: (date: Date) => void
  setEndTime: (time: Date) => void
}

/**
 * Action handlers for the form
 */
export interface FormHandlersIProps {
  onCreateMessage: (values: AnnouncementBannerFormData) => Promise<void>
  onDeleteMessage: () => Promise<void>
  handleOpenDeleteModal: () => void
  handleCloseDeleteModal: () => void
}

/**
 * Form state flags
 */
export interface FormStateIProps {
  isCreating: boolean
  isDeleting: boolean
  isViewMode: boolean
  isFormValid: boolean
  isDeleteModalOpen: boolean
}

/**
 * Return type for useAnnouncementBannerForm hook
 */
export interface UseAnnouncementBannerFormReturnIProps {
  form: UseFormMethods<AnnouncementBannerFormData>
  dates: DateTimeStateIProps
  handlers: FormHandlersIProps
  state: FormStateIProps
  currentMessage: ProductMessage | null
}

/**
 * Hook that encapsulates all form logic for the AnnouncementBannerSettings component.
 */
export const useAnnouncementBannerForm = (
  currentMessage: ProductMessage | null
): UseAnnouncementBannerFormReturnIProps => {
  const form = useForm<AnnouncementBannerFormData>({
    mode: 'onChange',
  })
  const { handleSubmit, errors, register, reset, watch, setValue } = form

  const [startDate, setStartDate] = useState<Date>(new Date())
  const [startTime, setStartTime] = useState<Date>(new Date())
  const [initialEndDate, setInitialEndDate] = useState<Date>(new Date())
  const [initialEndTime, setInitialEndTime] = useState<Date>(new Date())

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const { createProductMessage, isCreating } = useCreateProductMessage()
  const { deleteProductMessage, isDeleting } = useDeleteProductMessage()

  const isViewMode = !!currentMessage

  const { endDate, endTime, setEndDate, setEndTime } = useDateTimeValidation({
    startDate,
    startTime,
    initialEndDate,
    initialEndTime,
    isEnabled: !isViewMode,
  })

  useEffect(() => {
    if (currentMessage) {
      // Backend sends UTC, convert to PST for display
      const startDatePST = parseUTCToPST(currentMessage.startDateTime)
      const endDatePST = parseUTCToPST(currentMessage.endDateTime)

      reset({
        message: currentMessage.message,
      })

      setStartDate(startDatePST)
      setStartTime(startDatePST)
      setInitialEndDate(endDatePST)
      setInitialEndTime(endDatePST)
    } else {
      // Reset to defaults when no message
      reset({
        message: '',
      })
      const now = new Date()
      const oneMinuteLater = add(now, { minutes: 1 })
      setStartDate(now)
      setStartTime(now)
      setInitialEndDate(oneMinuteLater)
      setInitialEndTime(oneMinuteLater)
    }
  }, [currentMessage])

  const onCreateMessage = useCallback(
    async (values: AnnouncementBannerFormData) => {
      const result = await createProductMessage({
        message: values.message,
        startDateTime: formatDateForBackend(startDate, startTime),
        endDateTime: formatDateForBackend(endDate, endTime),
      })

      if (result.success) {
        reset()
      }
    },
    [createProductMessage, startDate, startTime, endDate, endTime, reset]
  )

  const onDeleteMessage = useCallback(async () => {
    if (!currentMessage) return

    const result = await deleteProductMessage({
      id: currentMessage.id,
    })

    if (result.success) {
      setIsDeleteModalOpen(false)
    }
  }, [currentMessage, deleteProductMessage])

  const handleOpenDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(true)
  }, [])

  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false)
  }, [])

  const messageValue = watch('message')
  const isFormValid = messageValue?.trim().length > 0 && !errors.message

  return {
    form: {
      handleSubmit,
      errors,
      register,
      reset,
      watch,
      setValue,
    } as UseFormMethods<AnnouncementBannerFormData>,
    dates: {
      startDate,
      startTime,
      endDate,
      endTime,
      setStartDate,
      setStartTime,
      setEndDate,
      setEndTime,
    },
    handlers: {
      onCreateMessage,
      onDeleteMessage,
      handleOpenDeleteModal,
      handleCloseDeleteModal,
    },
    state: {
      isCreating,
      isDeleting,
      isViewMode,
      isFormValid,
      isDeleteModalOpen,
    },
    currentMessage,
  }
}
